import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import ProtectedRoute from '@/components/layout/ProtectedRoute';
import Dashboard from '@/pages/Dashboard';
import Login from '@/pages/Login';
import NotFound from '@/pages/NotFound';
import CreateHoldRequestUnified from '@/pages/CreateHoldRequestUnified';
import HoldRequestDetail from '@/pages/HoldRequestDetail';
import LineItemDetail from '@/pages/LineItemDetail';
import ChartingQueue from '@/pages/ChartingQueue';
import ExceptionsQueue from '@/pages/ExceptionsQueue';
import NotificationSettings from '@/pages/NotificationSettings';
import Notifications from '@/pages/Notifications';
import MyRequests from '@/pages/MyRequests';
import UserManagement from '@/pages/UserManagement';
import AdminInventory from '@/pages/AdminInventory';
import AuthCallback from '@/pages/AuthCallback';
import AdminTools from '@/pages/AdminTools';
import AdminProductCatalog from '@/pages/AdminProductCatalog';
import Analytics from '@/pages/Analytics';

/**
 * Application router with protected routes and role-based access control
 */
const router = createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/auth/callback',
    element: <AuthCallback />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout>
          <Dashboard />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/holds',
    element: (
      <ProtectedRoute allowedRoles={['service_ae', 'sales_vp', 'gvp', 'rvp', 'admin', 'adops_leadership']}>
        <Layout>
          <MyRequests />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/holds/create',
    element: (
      <ProtectedRoute allowedRoles={['sales_ae', 'service_ae', 'sales_support', 'sales_vp', 'gvp']}>
        <Layout>
          <CreateHoldRequestUnified />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/holds/:id',
    element: (
      <ProtectedRoute>
        <Layout>
          <HoldRequestDetail />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/items/:id',
    element: (
      <ProtectedRoute>
        <Layout>
          <LineItemDetail />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/charting',
    element: (
      <ProtectedRoute allowedRoles={['charting_member', 'charting_manager']}>
        <Layout>
          <ChartingQueue />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/exceptions',
    element: (
      <ProtectedRoute allowedRoles={['exceptions_member', 'admin', 'sales_vp', 'gvp', 'rvp', 'adops_leadership']}>
        <Layout>
          <ExceptionsQueue />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/users',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'adops_leadership']}>
        <Layout>
          <UserManagement />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/inventory',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'adops_leadership']}>
        <Layout>
          <AdminInventory />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/product-catalog',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'adops_leadership']}>
        <Layout>
          <AdminProductCatalog />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/tools',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'adops_leadership']}>
        <Layout>
          <AdminTools />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/notifications',
    element: (
      <ProtectedRoute>
        <Layout>
          <Notifications />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <Layout>
          <NotificationSettings />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/analytics',
    element: (
      <ProtectedRoute allowedRoles={['adops_leadership', 'charting_manager', 'admin', 'sales_vp', 'gvp', 'rvp']}>
        <Layout>
          <Analytics />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '*',
    element: <NotFound />,
  },
]);

export default function AppRouter() {
  return <RouterProvider router={router} />;
}
